import { IntlShape } from 'react-intl'
import { FormikTouched, FormikErrors, FormikValues, FormikProps } from 'formik'

import { Node } from './model'
import GenericPaginationParams from './genericPaginationParams'

export interface Task {
  args: string
  clocked: null | string
  created_by: string
  crontab: null | string
  date_changed: string
  description: string
  enabled: boolean
  exchange: null
  expires: null
  headers: string
  id: number
  interval: IntervalSchedule | null
  is_app_pool: boolean
  is_running: boolean
  running_since: null | string
  kwargs: string
  last_run_at: null | string
  name: string
  next_execution: null | string
  one_off: false
  priority: null
  queue: null
  routing_key: null
  solar: null
  start_time: string
  state: null | string
  task: string
  total_run_count: number
  application: string
  node: string
  interface: string
}

export interface CreateOrEditTaskParams {
  name: string
  task: string
  enabled?: boolean
  schedule: IntervalSchedule | ClockedSchedule | CustomSchedule | ManualTask
  kwargs: Kwargs
}

export enum SchedulerType {
  INTERVAL = 'interval',
  CLOCKED = 'clocked',
  CRONTAB = 'crontab',
  MANUAL = 'manual'
}

export enum EmailNotificationMode {
  ALWAYS = 'always',
  ON_FAILURE = 'on_failure',
  ON_SUCCESS = 'on_success'
}

export enum IntervalPeriods {
  DAYS = 'days',
  HOURS = 'hours',
  MINUTES = 'minutes',
  SECONDS = 'seconds',
  MICROSECONDS = 'microseconds'
}

interface IntervalSchedule {
  every?: number
  period?: IntervalPeriods
  type?: SchedulerType.INTERVAL
}
interface ClockedSchedule {
  clocked_time?: string
  type?: SchedulerType.CLOCKED
}
interface ManualTask {
  type?: SchedulerType.MANUAL
}
interface CustomSchedule {
  timezone?: string
  minute?: string
  hour?: string
  day_of_week?: string
  day_of_month?: string
  month_of_year?: string
  type?: SchedulerType.CRONTAB
}
interface BaseForm {
  name: string
  enabled: boolean
  node_to_evaluate: Node | string | null
  interface_to_render: string | null
  interface_name: string | null
  app_path: string
  version?: string | null
}
export interface TaskForm extends BaseForm {
  schedule_type: SchedulerType
  period?: IntervalPeriods
  every?: number
  clockedTime: Date
  kwargs: object
  crontab_minute: number[]
  crontab_hour: number[]
  crontab_dayMonth: number[]
  crontab_month: number[]
  crontab_dayWeek: number[]
  timezone: string
}
export enum AppPoolOnDoneAction {
  KEEP_FOR_EVERYONE = 'keep_for_everyone',
  KEEP_FOR_USER = 'keep_for_user',
  KEEP_FOR_DEPARTMENTS = 'keep_for_departments'
}
export enum AppPoolExpirationMode {
  NONE = 'none',
  TIME_OF_DAY = 'time_of_day',
  MAX_AGE = 'max_age'
}
/** Units offered for the max age of a pool instance. */
export const APP_POOL_MAX_AGE_PERIODS: IntervalPeriods[] = [
  IntervalPeriods.MINUTES,
  IntervalPeriods.HOURS,
  IntervalPeriods.DAYS
]
/** Below this, an instance would be recycled before it is of any use. */
export const APP_POOL_MIN_MAX_AGE_MINUTES = 10
export const APP_POOL_MAX_AGE_PERIOD_MINUTES: Record<string, number> = {
  [IntervalPeriods.MINUTES]: 1,
  [IntervalPeriods.HOURS]: 60,
  [IntervalPeriods.DAYS]: 24 * 60
}
export interface AppPoolMaxAge {
  every: number
  period: IntervalPeriods
}
/** A stretch of the week the pool is kept up in. `days` is cron-numbered
 *  (Sunday is 0); a `to` earlier than `from` crosses midnight. */
export interface AppPoolActivePeriod {
  days: number[]
  from: string
  to: string
}
/** The same period while it is edited, with the bounds the TimePicker holds. */
export interface AppPoolActivePeriodForm {
  days: number[]
  from: Date
  to: Date
}
export const APP_POOL_DEFAULT_PERIOD_FROM_HOUR = 8
export const APP_POOL_DEFAULT_PERIOD_TO_HOUR = 18
export interface AppPoolForm extends BaseForm {
  kwargs: object
  expirationMode: AppPoolExpirationMode
  instances: number
  maxOpenInstancesEnabled: boolean
  maxOpenInstances: number
  expireat: Date
  maxAgeEvery: number
  maxAgePeriod: IntervalPeriods
  activePeriodsEnabled: boolean
  activePeriods: AppPoolActivePeriodForm[]
  timezone: string
  userCompanyId: number | null
  departmentIds: number[]
  action: AppPoolOnDoneAction
}
interface TaskLogBaseParams {
  page: number
  page_size: number
  order_by: string
}

export interface TaskLogsParams extends TaskLogBaseParams, SearchFormProps {
  periodic_task_id: number
}
export interface TaskLogDetailParams extends TaskLogBaseParams {
  periodic_task_log_id: number | string
  search_text: string
}
export interface TaskLog {
  id: string
  created_at: string
  params: object | null
  periodic_task: number
  state: string
  task_id: string
}
export interface TaskLogDetail {
  id: string
  created_at: string
  activity: null | string
  description: string
  info: object
  is_running: boolean
  periodic_task_log: string
  state: ScheduleTaskStates
  updated_at: string
}

export interface AdvanceFiltersFormProps {
  clearSearchParams: () => void
  timeZones: string[]
  location: string
  getFilterPeriodicTaskLogs: (values: SearchFormProps) => void
}
export interface SearchFormProps {
  max_date?: Date | string
  min_date?: Date | string
  params?: string
  state?: string
  timezone?: string
}
export type ParamsModalProps = {
  openDialog: boolean
  title: string
  closeModal: () => void
  handleRunwithParams: (formValues: TaskParams) => void
  task: Task | null
}
export interface TaskParams {
  [name: string]: Params
}
export interface Params {
  type: 'select' | 'input' | 'checkbox'
  name: string
  label: string
  default_value: string
  source: string
  value?: string | boolean
}
export interface TaskData {
  id: number
  kwargs: Kwargs
}
export interface Kwargs {
  company_id?: number
  app_path: string
  node_id?: string
  interface_id?: string
  interface_name?: string
  schedule_id?: string
  usercompany_id?: number
  params?: TaskParams
  on_done?: OnDone
  notification_users: string[]
  email_notification_mode?: EmailNotificationMode
  resources?: string
  is_manual?: boolean
  version?: string
}
export interface OnDone {
  action: AppPoolOnDoneAction
  instances?: string
  /** Cap on free pool instances plus the departments' open sessions. */
  max_open_instances?: number
  usercompany_id?: number
  department_ids?: number[]
  expiration_mode?: AppPoolExpirationMode
  expireat?: string
  max_age?: AppPoolMaxAge
  active_periods?: AppPoolActivePeriod[]
  /** Applies to every time in the configuration, `expireat` included. */
  timezone?: string
}
export type DeleteTaskDialogProps = {
  openDialog: boolean
  title: string
  message: string
  closeModal: () => void
  handleDeleteTask: () => void
}

export interface SchedulerProps {
  values: FormikValues
  errors: FormikErrors<any>
  touched: FormikTouched<any>
  setFieldTouched: (field: string) => void
  setFieldValue: (field: string, value: any) => void
}

export interface StepProps {
  formProps: FormikProps<any>
  taskId?: number
  taskKwargs?: string
}
export interface GeneralOptionsProps extends StepProps {
  nameError: boolean
  setNameError: (value: boolean) => void
}
export interface ParamsProps extends StepProps {
  userId: number
  setUserId: React.Dispatch<React.SetStateAction<number>>
  notificationUsers: string[]
  setNotificationUsers: React.Dispatch<React.SetStateAction<string[]>>
  emailNotificationMode: EmailNotificationMode
  setEmailNotificationMode: React.Dispatch<React.SetStateAction<EmailNotificationMode>>
  resources: string
  setResources: React.Dispatch<React.SetStateAction<string>>
}
export interface SchedulerBaseProps extends StepProps {
  timeZones: string[]
}

export interface DayOrMonth {
  value: number
  label: string
}

export interface CustomScheduler extends SchedulerProps {
  timeZones: string[]
}

export type TaskStatusProps = {
  status: string | null
  noBgColor?: boolean
  customFailureColor?: string
  customSuccessColor?: string
  customSuccessLabel?: string
  customFailureLabel?: string
  customInProgressLabel?: string
}
/** Columns of the scheduled tasks table that offer a per-column filter. */
export enum TaskFilterColumn {
  Name = 'name',
  Application = 'application',
  Node = 'node',
  CreatedBy = 'created_by',
  LastStatus = 'last_status',
  IsAppPool = 'is_app_pool',
  Enabled = 'enabled'
}

/** Columns filtered by yes/no instead of by their text. */
export const TASK_BOOLEAN_FILTER_COLUMNS: TaskFilterColumn[] = [
  TaskFilterColumn.IsAppPool,
  TaskFilterColumn.Enabled
]

/** Selected values per column, as sent to the API in the `filters` param. */
export type TaskColumnFilters = Partial<Record<TaskFilterColumn, string[]>>

/** Value standing for tasks with no application/node/executor, or that never ran. */
export const TASK_FILTER_NO_VALUE = '__no_value__'

/** Prefix of the filter values of id-backed columns (today only Name). Filtering
 *  by id instead of by the text shown keeps a saved filter working when the task
 *  is renamed. Values without it come from filters saved before that change and
 *  still match against the task name. */
export const TASK_FILTER_ID_PREFIX = 'id:'

export interface ScheduledTasksListParams extends GenericPaginationParams {
  /** JSON-encoded {@link TaskColumnFilters}. */
  filters?: string
  hide_disabled?: boolean
  show_system_tasks?: boolean
}

export interface TaskFilterValuesParams {
  column: TaskFilterColumn
  filters?: string
  search_text?: string
  apply_filters?: string
  hide_disabled?: boolean
  show_system_tasks?: boolean
}

export interface TaskFilterValuesResponse {
  values: string[]
  /** `{value: text to show}`, only filled in for id-backed columns. A value with
   *  no entry here is shown as-is. */
  labels?: Record<string, string>
}

export enum ScheduleTaskStates {
  PROGRESS = 'PROGRESS',
  INFO = 'INFO',
  WARNING = 'WARNING',
  FAILURE = 'FAILURE',
  RETRY = 'RETRY',
  SUCCESS = 'SUCCESS',
  REVOKED = 'REVOKED',
  STARTED = 'STARTED',
  PENDING = 'PENDING',
  RECEIVED = 'RECEIVED'
}
export interface CrontabResultProps {
  intl: IntlShape
  crontab_dayMonth: []
  crontab_hour: []
  crontab_minute: []
  crontab_month: []
  crontab_dayWeek: []
}
export enum CrontabFields {
  CRONTAB_DAYMONTH = 'crontab_dayMonth',
  CRONTAB_HOUR = 'crontab_hour',
  CRONTAB_MINUTE = 'crontab_minute',
  CRONTAB_MONTH = 'crontab_month',
  CRONTAB_DAYWEEK = 'crontab_dayWeek'
}
export interface SelectNodeProps extends StepProps {
  selectedAppPath: string
  appPathChange: boolean
  setAppPathChange: (value: boolean) => void
  isDense?: boolean
  selectedAppVersion?: string
}

export interface FileManagerProps extends StepProps {
  selectedModelPath: string
  setSelectedModelPath: (value: string) => void
  setModelPathChange: (value: boolean) => void
}
